import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDimensions, getMeasures } from "./api";
import { DimensionInfo, MeasureInfo } from "./types";

export const CUBE_NAME = "Maxi-cube";

type LoadStatus = "idle" | "loading" | "loaded" | "failed";

interface CubeState {
  dimensions: DimensionInfo[];
  measures: MeasureInfo[];
  dimensionsStatus: LoadStatus;
  measuresStatus: LoadStatus;
  loading: boolean;
  error: string | null;
}

const initialState: CubeState = {
  dimensions: [],
  measures: [],
  dimensionsStatus: "idle",
  measuresStatus: "idle",
  loading: false,
  error: null,
};

type CubeRootState = { cube: CubeState };

// Pas de nouvel appel si déjà chargé ou en cours (montages multiples, StrictMode)
const shouldFetch = (status: LoadStatus) => status === "idle" || status === "failed";

export const fetchDimensions = createAsyncThunk<DimensionInfo[], void, { state: CubeRootState }>(
  "cube/fetchDimensions",
  async () => {
    const response = await getDimensions(CUBE_NAME);
    return response.data ?? [];
  },
  { condition: (_, { getState }) => shouldFetch(getState().cube.dimensionsStatus) },
);

export const fetchMeasures = createAsyncThunk<MeasureInfo[], void, { state: CubeRootState }>(
  "cube/fetchMeasures",
  async () => {
    const response = await getMeasures(CUBE_NAME);
    return response.data ?? [];
  },
  { condition: (_, { getState }) => shouldFetch(getState().cube.measuresStatus) },
);

const cubeSlice = createSlice({
  name: "cube",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDimensions.pending, (state) => {
        state.dimensionsStatus = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDimensions.fulfilled, (state, action) => {
        state.dimensionsStatus = "loaded";
        state.loading = false;
        state.dimensions = action.payload;
      })
      .addCase(fetchDimensions.rejected, (state, action) => {
        state.dimensionsStatus = "failed";
        state.loading = false;
        state.error = action.error.message || "Failed";
      })
      .addCase(fetchMeasures.pending, (state) => {
        state.measuresStatus = "loading";
      })
      .addCase(fetchMeasures.fulfilled, (state, action) => {
        state.measuresStatus = "loaded";
        state.measures = action.payload;
      })
      .addCase(fetchMeasures.rejected, (state) => {
        state.measuresStatus = "failed";
      });
  },
});

export default cubeSlice.reducer;
